import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getSiteSettings, absoluteUrl } from "@/lib/api";
import { applySettings } from "@/lib/theme";

const SettingsContext = createContext(null);
const CACHE_KEY = "nelum_site_settings";
const DEFAULTS = {
  font_pair: "ceylon",
  theme: "nelum",
  logo_tint: true,
  whatsapp_enabled: true,
  whatsapp_number: "+62 21 1234 5678",
  whatsapp_message: "Hello Nelum Dining, I would like to make an enquiry.",
  whatsapp_label: "Chat with Nelum Dining on WhatsApp",
  menu_display_mode: "existing",
  menu_pdf_url: "",
  menu_pdf_pages: [],
  menu_pdf_categories: [],
  menu_hero_visible: true,
};

function menuPdfCategories(s) {
  if (Array.isArray(s.menu_pdf_categories) && s.menu_pdf_categories.length) {
    return s.menu_pdf_categories;
  }
  if (s.menu_pdf_url) {
    return [{
      id: "food-menu",
      label: "Food Menu",
      pdf_url: s.menu_pdf_url,
      pages: Array.isArray(s.menu_pdf_pages) ? s.menu_pdf_pages : [],
    }];
  }
  return [];
}

function applyFavicon(url) {
  if (typeof document === "undefined") return;
  const href = url ? absoluteUrl(url) : null;
  if (!href) return; // keep the built-in favicon from public/index.html
  let link = document.querySelector('link[rel="icon"][data-dynamic="true"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.setAttribute("data-dynamic", "true");
    document.head.appendChild(link);
  }
  link.href = href;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.font_pair && parsed.theme) return parsed;
  } catch (err) {
    console.warn("[SettingsContext] Failed to read cached settings", err);
  }
  return null;
}

function writeCache(s) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        font_pair: s.font_pair,
        theme: s.theme,
        logo_tint: s.logo_tint !== false,
        whatsapp_enabled: s.whatsapp_enabled !== false,
        whatsapp_number: s.whatsapp_number || "",
        whatsapp_message: s.whatsapp_message || "",
        whatsapp_label: s.whatsapp_label || "",
        menu_display_mode: s.menu_display_mode || "existing",
        menu_pdf_url: s.menu_pdf_url || "",
        menu_pdf_pages: Array.isArray(s.menu_pdf_pages) ? s.menu_pdf_pages : [],
        menu_pdf_categories: menuPdfCategories(s),
        menu_hero_visible: s.menu_hero_visible !== false,
      }),
    );
  } catch (err) {
    console.warn("[SettingsContext] Failed to persist settings cache", err);
  }
}

// Apply cache SYNCHRONOUSLY on module import so the very first paint uses the last-known theme.
const initial = readCache() || DEFAULTS;
applySettings(initial);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(initial);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await getSiteSettings();
      applySettings(s);
      applyFavicon(s.favicon_url);
      writeCache(s);
      setSettings({
        ...s,
        menu_pdf_categories: menuPdfCategories(s),
      });
    } catch (err) {
      console.warn("[SettingsContext] Failed to fetch site settings — using defaults", err);
      applySettings(DEFAULTS);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setLocal = (partial) => {
    setSettings((s) => {
      const next = { ...s, ...partial };
      applySettings(next);
      if ("favicon_url" in partial) applyFavicon(next.favicon_url);
      writeCache(next);
      return next;
    });
  };

  return (
    <SettingsContext.Provider value={{ settings, ready, refresh, setLocal }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext) || { settings: DEFAULTS, ready: false };
